/**
 * FormVersionEditorPage — éditeur de schéma d'un brouillon (F2-Forms-1c-ii).
 *
 * Charge template + version, applique les gardes (404 / mismatch template↔version
 * / version publiée → lecture seule / template archivé → lecture seule), puis
 * délègue l'édition à <SchemaEditor> (contrôlé). Save explicite (PATCH { schema, expected_revision }),
 * validation draft permissive côté client, dirty + unsaved guard, gestion 409
 * publiée/archivée sans perte silencieuse. Aucun aperçu, aucune publication (1c-iii).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { FormRenderer } from "../../components/forms/FormRenderer";
import { ApiException } from "../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../lib/apiErrorMessage";
import {
  type FormTemplateRow,
  type FormTemplateVersionRow,
  useFormTemplate,
  useFormVersion,
  usePatchFormVersion,
} from "../../lib/queries/formTemplates";
import { toast } from "../../lib/toast";
import { useUnsavedChangesGuard } from "../../lib/unsavedChanges/UnsavedChangesContext";

import {
  messagesDIssues,
  toWireData,
  validateSubmitData,
  type FieldIssue,
} from "./_parts/fillData";
import { SchemaEditor } from "./_parts/SchemaEditor";
import type { ImportedSchema } from "./_parts/schemaImport";
import { SchemaImportPanel } from "./_parts/SchemaImportPanel";
import * as M from "./_parts/schemaModel";
import { describeDraftIssue } from "./_parts/schemaValidation";

export function FormVersionEditorPage() {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const { id, versionId } = useParams<{ id: string; versionId: string }>();

  const templateQ = useFormTemplate(id);
  const versionQ = useFormVersion(versionId);

  if ((templateQ.isLoading && !templateQ.data) || (versionQ.isLoading && !versionQ.data)) {
    return <EditorSkeleton />;
  }

  const notFound =
    (templateQ.error instanceof ApiException && templateQ.error.status === 404) ||
    (versionQ.error instanceof ApiException && versionQ.error.status === 404);

  if (notFound || templateQ.error || versionQ.error) {
    return (
      <ErrorPanel
        message={notFound ? t("form_templates.editor.not_found") : t("errors.generic")}
        backTo={id ? `/formulaires/${id}` : "/formulaires"}
      />
    );
  }

  const template = templateQ.data?.template;
  const version = versionQ.data;
  if (!template || !version) return null;

  // Garde cohérence : la version doit appartenir à ce template (anti-leak → 404).
  if (version.template_id !== template.id) {
    return (
      <ErrorPanel
        message={t("form_templates.editor.not_found")}
        backTo={`/formulaires/${template.id}`}
      />
    );
  }

  const readOnlyReason: "published" | "archived" | null =
    template.status === "archived"
      ? "archived"
      : version.published_at !== null
        ? "published"
        : null;

  return (
    <Editor
      key={version.id}
      template={template}
      version={version}
      lang={lang}
      readOnlyReason={readOnlyReason}
      onReloadVersion={() => {
        void versionQ.refetch();
        void templateQ.refetch();
      }}
    />
  );
}

function Editor(props: {
  template: FormTemplateRow;
  version: FormTemplateVersionRow;
  lang: "fr" | "en";
  readOnlyReason: "published" | "archived" | null;
  onReloadVersion: () => void;
}) {
  const { template, version, lang, readOnlyReason, onReloadVersion } = props;
  const { t } = useTranslation();
  const patch = usePatchFormVersion(version.id, template.id);

  // schema (édition) + baseline (dernier schéma sauvegardé) partagent les mêmes
  // `_uid` initiaux → le verrouillage des clés suit l'identité des éléments
  // persistés (persistedUids), pas la valeur courante des clés.
  const [schema, setSchema] = useState<M.EditorSchema>(() => M.toEditorSchema(version.schema));
  const writeInFlight = useRef(false);
  const [savedSchema, setSavedSchema] = useState(version.schema);
  const [revision, setRevision] = useState(version.revision);
  const [baseline, setBaseline] = useState<M.EditorSchema>(schema);
  const [persistedUids, setPersistedUids] = useState<Set<string>>(() => M.collectUids(schema));
  const [conflict, setConflict] = useState<"published" | "archived" | "stale" | null>(null);
  const [validationAttempt, setValidationAttempt] = useState(0);
  const validationSummary = useRef<HTMLDivElement>(null);
  const validationIssues = useMemo(
    () => (validationAttempt ? M.validateDraft(schema).issues : []),
    [schema, validationAttempt],
  );
  const describedIssues = validationIssues.map((issue) =>
    describeDraftIssue(issue, schema, lang, t),
  );
  const fieldErrors = Object.fromEntries(
    describedIssues
      .filter((issue) => issue.targetId)
      .map((issue) => [issue.targetId!, issue.message]),
  );
  useEffect(() => {
    if (validationAttempt) validationSummary.current?.focus();
  }, [validationAttempt]);
  const [tab, setTab] = useState<"edit" | "preview">("edit");
  const [previewValues, setPreviewValues] = useState<Record<string, unknown>>({});
  const [previewErrors, setPreviewErrors] = useState<Record<string, FieldIssue>>({});

  const baselineJson = useMemo(() => JSON.stringify(M.serializeSchema(baseline)), [baseline]);
  const currentJson = JSON.stringify(M.serializeSchema(schema));
  const dirty = currentJson !== baselineJson;

  const readOnly = readOnlyReason !== null;
  const { allowNavigation } = useUnsavedChangesGuard(
    dirty && !readOnly,
    t("form_templates.editor.unsaved_message"),
  );

  const persistSchema = (nextSchema: unknown, expectedRevision: string) => {
    if (
      readOnly ||
      conflict ||
      patch.isPending ||
      writeInFlight.current ||
      expectedRevision !== revision
    )
      return;
    writeInFlight.current = true;
    setValidationAttempt(0);
    patch.mutate(
      { schema: nextSchema, expected_revision: expectedRevision },
      {
        onSuccess: (updated) => {
          setSavedSchema(updated.schema);
          setRevision(updated.revision);
          const fresh = M.toEditorSchema(updated.schema);
          setBaseline(fresh);
          setSchema(fresh);
          setPersistedUids(M.collectUids(fresh));
          allowNavigation();
          toast.success(t("form_templates.editor.saved"));
        },
        onSettled: () => {
          writeInFlight.current = false;
        },
        onError: (err) => {
          if (err instanceof ApiException && err.status === 409) {
            if (err.apiError.code === "FORM_VERSION_EDIT_CONFLICT") return setConflict("stale");
            if (err.apiError.code === "FORM_VERSION_PUBLISHED_IMMUTABLE")
              return setConflict("published");
            if (err.apiError.code === "FORM_TEMPLATE_ARCHIVED") return setConflict("archived");
          }
        },
      },
    );
  };

  const onSave = () => {
    if (!dirty) return;
    const check = M.validateDraft(schema);
    if (!check.ok) {
      setValidationAttempt((attempt) => attempt + 1);
      setTab("edit");
      return;
    }
    persistSchema(M.serializeSchema(schema), revision);
  };

  const onImport = (candidate: ImportedSchema, expectedRevision: string) => {
    if (dirty) return;
    persistSchema(candidate, expectedRevision);
  };

  // Aperçu : validation réaliste (mode submit) déclenchée explicitement — jamais
  // au premier champ. N'a AUCUN effet métier (pas de submit, pas de réseau).
  const onValidatePreview = () => {
    const contract = M.serializeSchema(schema) as Parameters<typeof toWireData>[1];
    setPreviewErrors(validateSubmitData(toWireData(previewValues, contract), contract));
  };

  const genericError =
    patch.isError &&
    !(
      patch.error instanceof ApiException &&
      patch.error.status === 409 &&
      (patch.error.apiError.code === "FORM_VERSION_PUBLISHED_IMMUTABLE" ||
        patch.error.apiError.code === "FORM_TEMPLATE_ARCHIVED")
    )
      ? patch.error instanceof ApiException
        ? getApiErrorMessage(patch.error, lang) || t("errors.generic")
        : t("errors.generic")
      : null;

  return (
    <div className="mx-auto w-full max-w-3xl px-1 py-6">
      <nav className="mb-4 text-sm">
        <Link
          to={`/formulaires/${template.id}`}
          className="hover:text-brand-700 focus-visible:ring-brand-500 rounded-sm text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
        >
          ← {t("form_templates.detail.back_to_list")}
        </Link>
      </nav>

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            {t("form_templates.editor.title", {
              name: template.name,
              version: version.version_number,
            })}
          </h1>
          <p className="mt-1 text-sm text-slate-500">{t("form_templates.editor.subtitle")}</p>
        </div>
        {!readOnly && !conflict && (
          <button
            type="button"
            onClick={onSave}
            disabled={!dirty || patch.isPending}
            aria-busy={patch.isPending}
            className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-500 inline-flex shrink-0 touch-manipulation items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            {patch.isPending ? t("form_templates.editor.saving") : t("form_templates.editor.save")}
          </button>
        )}
      </header>

      <details className="mt-4 rounded-lg border border-slate-200 bg-white p-3 text-sm">
        <summary className="focus-visible:ring-brand-500 cursor-pointer rounded font-medium text-slate-700 focus-visible:outline-none focus-visible:ring-2">
          {t("form_templates.editor.version_information")}
        </summary>
        <label className="mt-3 block">
          <span className="font-medium text-slate-700">
            {t("form_templates.editor.loaded_revision")}
          </span>
          <textarea
            readOnly
            rows={3}
            value={revision}
            spellCheck={false}
            aria-describedby="loaded-revision-help"
            className="focus-visible:ring-brand-500 mt-1 block w-full rounded border border-slate-300 bg-slate-50 p-2 font-mono text-xs text-slate-700 focus-visible:outline-none focus-visible:ring-2"
          />
        </label>
        <p id="loaded-revision-help" className="mt-2 text-slate-600">
          {t("form_templates.editor.loaded_revision_help")}
        </p>
      </details>

      {!readOnly && (
        <SchemaImportPanel
          key={`${version.id}:${revision}`}
          target={{
            tenantId: version.tenant_id,
            templateId: template.id,
            templateName: template.name,
            versionId: version.id,
            versionNumber: version.version_number,
          }}
          baseline={savedSchema as ImportedSchema}
          revision={revision}
          blocked={conflict !== null || patch.isPending}
          dirty={dirty}
          onConfirm={onImport}
        />
      )}

      {/* Bannière lecture seule (état chargé) */}
      {readOnly && <ReadOnlyBanner reason={readOnlyReason} />}

      {/* Note de version (changelog) — visible en consultation d'une version.
          Aucun bloc si changelog null/blank. */}
      {version.changelog && version.changelog.trim() !== "" && (
        <div className="mt-4 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm shadow-sm">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
            {t("form_templates.publish.changelog_label")}
          </p>
          <p className="mt-1 break-words text-slate-700">{version.changelog}</p>
        </div>
      )}

      {/* Bannière conflit 409 (survenu au Save) — modifications locales conservées */}
      {conflict && (
        <div
          role="alert"
          className="mt-5 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800"
        >
          <p className="font-medium">
            {conflict === "stale"
              ? t("form_templates.editor.conflict_stale_title")
              : conflict === "published"
                ? t("form_templates.editor.conflict_published_title")
                : t("form_templates.editor.conflict_archived_title")}
          </p>
          <p className="mt-1">
            {t(
              conflict === "stale"
                ? "form_templates.editor.conflict_stale_body"
                : "form_templates.editor.conflict_body",
            )}
          </p>
          {conflict === "stale" ? (
            <a
              href={`/formulaires/${template.id}/versions/${version.id}/edit`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-block font-semibold underline"
            >
              {t("form_templates.editor.conflict_open_latest")}
            </a>
          ) : (
            <button
              type="button"
              onClick={onReloadVersion}
              className="mt-2 touch-manipulation rounded-md border border-amber-400 bg-white px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2"
            >
              {t("form_templates.editor.conflict_reload")}
            </button>
          )}
        </div>
      )}

      {genericError && (
        <div
          role="alert"
          className="mt-5 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
        >
          {genericError}
        </div>
      )}

      {describedIssues.length > 0 && (
        <div
          ref={validationSummary}
          tabIndex={-1}
          aria-label={t("form_templates.editor.validation_failed")}
          role="alert"
          className="mt-5 rounded-md border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-700"
        >
          <p className="font-medium">{t("form_templates.editor.validation_failed")}</p>
          <ul className="mt-1 list-disc pl-5">
            {describedIssues.map((issue, i) => (
              <li key={`${issue.targetId ?? "schema"}-${i}`} className="break-words">
                {issue.targetId ? (
                  <button
                    type="button"
                    className="min-h-11 rounded text-left underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
                    onClick={() => document.getElementById(issue.targetId!)?.focus()}
                  >
                    {issue.label} — {issue.message}
                  </button>
                ) : (
                  <span>
                    {issue.label} — {issue.message}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Onglets Édition | Aperçu (pas de route dédiée — décision PO D2). */}
      <div
        role="tablist"
        aria-label={t("form_templates.editor.tabs_label")}
        className="mt-6 inline-flex rounded-lg border border-slate-200 bg-white p-0.5 text-sm shadow-sm"
      >
        {(["edit", "preview"] as const).map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            onClick={() => setTab(key)}
            className={
              tab === key
                ? "bg-brand-600 focus-visible:ring-brand-500 rounded-md px-3 py-1.5 text-sm font-medium text-white shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                : "focus-visible:ring-brand-500 rounded-md px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
            }
          >
            {t(`form_templates.editor.tab_${key}`)}
          </button>
        ))}
      </div>

      {tab === "edit" ? (
        <div className="mt-4">
          <label className="mb-5 block">
            <span className="label">{t("request_flow.purpose")}</span>
            <select
              className="input"
              disabled={readOnly || conflict !== null || patch.isPending}
              value={schema.version_metadata.purpose ?? ""}
              onChange={(e) =>
                setSchema({
                  ...schema,
                  version_metadata: {
                    schema_version: 1,
                    ...(e.target.value
                      ? { purpose: e.target.value as "mandate" | "business_spec" | "other" }
                      : {}),
                  },
                })
              }
            >
              <option value="">{t("request_flow.unclassified")}</option>
              {(["mandate", "business_spec", "other"] as const).map((p) => (
                <option key={p} value={p}>
                  {t(`request_flow.purpose_${p}`)}
                </option>
              ))}
            </select>
            <span className="mt-1 block text-sm text-slate-600">
              {t("request_flow.purpose_help")}
            </span>
          </label>
          <SchemaEditor
            value={schema}
            onChange={setSchema}
            errors={fieldErrors}
            persistedUids={persistedUids}
            defaultLang={template.default_language}
            disabled={readOnly || conflict !== null || patch.isPending}
          />
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-slate-500">{t("form_templates.editor.preview_note")}</p>
            <button
              type="button"
              onClick={onValidatePreview}
              className="focus-visible:ring-brand-500 inline-flex touch-manipulation items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
            >
              {t("form_templates.editor.validate_preview")}
            </button>
          </div>
          <FormRenderer
            schema={schema}
            locale={lang}
            mode={readOnly ? "readonly" : "preview"}
            values={previewValues}
            onValuesChange={setPreviewValues}
            errors={messagesDIssues(previewErrors, t)}
          />
        </div>
      )}
    </div>
  );
}

function ReadOnlyBanner({ reason }: { reason: "published" | "archived" | null }) {
  const { t } = useTranslation();
  return (
    <div
      role="status"
      className="mt-5 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700"
    >
      <p className="font-medium">
        {reason === "archived"
          ? t("form_templates.editor.readonly_archived_title")
          : t("form_templates.editor.readonly_published_title")}
      </p>
      <p className="mt-1 text-slate-600">
        {reason === "archived"
          ? t("form_templates.editor.readonly_archived_body")
          : t("form_templates.editor.readonly_published_body")}
      </p>
    </div>
  );
}

function ErrorPanel({ message, backTo }: { message: string; backTo: string }) {
  const { t } = useTranslation();
  return (
    <div className="mx-auto w-full max-w-3xl px-1 py-8">
      <div
        role="alert"
        className="rounded-xl border border-red-200 bg-red-50 px-4 py-4 text-sm text-red-700"
      >
        <p className="font-medium">{message}</p>
        <Link
          to={backTo}
          className="mt-3 inline-flex rounded-md border border-red-300 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
        >
          {t("form_templates.detail.back_to_list")}
        </Link>
      </div>
    </div>
  );
}

function EditorSkeleton() {
  return (
    <div className="mx-auto w-full max-w-3xl px-1 py-6">
      <div className="h-4 w-24 rounded bg-slate-100 motion-safe:animate-pulse motion-reduce:animate-none" />
      <div className="mt-4 h-8 w-72 rounded bg-slate-200 motion-safe:animate-pulse motion-reduce:animate-none" />
      <div className="mt-6 h-64 rounded-xl bg-slate-100 motion-safe:animate-pulse motion-reduce:animate-none" />
    </div>
  );
}
